import React, { useState, useEffect } from "react"

import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import BackgroundColorToggle from "../shared/BackgroundColorToggle"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"

import EditorFooter from "./shared/EditorFooter"

const ReviewsEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()
  const [currentPage, setCurrentPage] = useState(1)
  const reviewsPerPage = 10
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [formData, setFormData] = useState({
    headline: section?.headline || "Client Reviews",
    subheadline: section?.subheadline || "See what our clients are saying",
    body: section?.body || "",
    imageUrl: section?.imageUrl || practice?.reviewsSectionImageUrl || "",
    settings: section?.settings || {
      displayCount: 3,
      showRatings: true,
      showPhotos: true,
      layout: "cards",
      selectedReviewIds: [],
      showVideoReviews: true,
      videoReviewsCount: 3,
      selectedVideoReviewIds: [],
      showTextReviews: true,
      textReviewsCount: 3,
      showDates: true,
      showOverallRating: true,
      showCTA: true,
      ctaText: "View all reviews",
      showBackgroundColor: false,
      backgroundColor: "#ffffff",
      showTextColor: false,
      textColor: "#55433f"
    }
  })

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "Client Reviews",
        subheadline: section.subheadline || "See what our clients are saying",
        body: section.body || "",
        imageUrl: section.imageUrl || practice?.reviewsSectionImageUrl || "",
        settings: {
          displayCount: 3,
          showRatings: true,
          showPhotos: true,
          layout: "cards",
          selectedReviewIds: [],
          showVideoReviews: true,
          videoReviewsCount: 3,
          selectedVideoReviewIds: [],
          showTextReviews: true,
          textReviewsCount: 3,
          showDates: true,
          showOverallRating: true,
          showCTA: true,
          ctaText: "View all reviews",
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => {
      const newSettings = { ...prev.settings, [key]: value }

      // Immediately update preview for important settings (avoids useEffect loops)
      const importantSettings = [
        "displayCount",
        "showRatings",
        "showPhotos",
        "layout",
        "showVideoReviews",
        "videoReviewsCount",
        "selectedVideoReviewIds",
        "selectedReviewIds",
        "showTextReviews",
        "textReviewsCount",
        "showDates",
        "showOverallRating",
        "showCTA",
        "ctaText",
        "showBackgroundColor",
        "backgroundColor",
        "showTextColor",
        "textColor"
      ]
      if (section?.id && importantSettings.includes(key)) {
        handleUpdateSectionPreview(section.id, {
          ...section,
          ...prev,
          settings: newSettings
        })
      }

      return {
        ...prev,
        settings: newSettings
      }
    })
  }

  const handleReviewSelection = (reviewId) => {
    const currentSelected = formData.settings.selectedReviewIds || []
    const newSelected = currentSelected.includes(reviewId)
      ? currentSelected.filter((id) => id !== reviewId)
      : [...currentSelected, reviewId]

    handleSettingsChange("selectedReviewIds", newSelected)
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    setShowConfirmModal(true)
  }

  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    onSave({
      layout: "reviews",
      ...formData
    })
  }

  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  // Get all reviews from practice
  const allReviews = practice?.reviews || []

  // Calculate pagination
  const totalPages = Math.ceil(allReviews.length / reviewsPerPage)
  const startIndex = (currentPage - 1) * reviewsPerPage
  const endIndex = startIndex + reviewsPerPage
  const availableReviews = allReviews.slice(startIndex, endIndex)

  // Get full client name from review
  const getClientName = (review) => {
    if (review.isAnonymous) {
      return "Anonymous"
    }

    const firstName = review.client?.firstName || review.user?.firstName || ""
    const lastName = review.client?.lastName || review.user?.lastName || ""

    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    } else if (firstName) {
      return firstName
    } else if (review.client?.name) {
      return review.client.name
    }

    return "Anonymous"
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
      // Scroll to top of reviews list when page changes
      const reviewsContainer = document.querySelector("[data-reviews-container]")
      if (reviewsContainer) {
        reviewsContainer.scrollIntoView({ behavior: "smooth", block: "start" })
      }
    }
  }

  // Reset to page 1 when reviews change
  useEffect(() => {
    setCurrentPage(1)
  }, [allReviews.length])

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`text-sm ${i < rating ? "text-yellow-400" : "text-gray-300"}`}>
        ★
      </span>
    ))

  return (
    <>
      {/* Form Content */}
      <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-8 pt-6">
        {/* Review Selection */}
        <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-6 px-0 pb-6 pt-0">
          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-0.5 p-0 text-left leading-[0]">
            <div className="relative w-full shrink-0 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-[#0b3954]">
              <p className="block leading-[24px]">Select reviews to feature</p>
            </div>
            <div className="relative w-full shrink-0 font-['Manrope:Regular',_sans-serif] text-[14px] font-normal text-[#55778a]">
              <p className="block leading-[20px]">Choose which reviews to display in this section.</p>
            </div>
          </div>

          {allReviews.length > 0 ? (
            <>
              {/* Pagination Info - Show if there are more reviews than per page */}
              {allReviews.length > reviewsPerPage && (
                <div className="relative mb-3 box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between gap-4 rounded-lg border border-[#c8d6df] bg-[#f5fafd] p-3">
                  <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#55778a]">
                    <p className="block leading-[20px]">
                      Showing {startIndex + 1}-{Math.min(endIndex, allReviews.length)} of {allReviews.length} reviews
                    </p>
                  </div>
                  <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-2 p-0">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 overflow-visible rounded border px-3 py-1.5 transition-colors ${
                        currentPage === 1
                          ? "cursor-not-allowed border-[#c8d6df] bg-white text-[#c8d6df]"
                          : "border-[#0d9da4] bg-white text-[#0d9da4] hover:bg-[#f5fafd]"
                      }`}>
                      <div className="relative shrink-0 text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0]">
                        <p className="block whitespace-pre leading-[20px]">Previous</p>
                      </div>
                    </button>
                    <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[14px] font-normal leading-[0] text-[#55778a]">
                      <p className="block leading-[20px]">
                        Page {currentPage} of {totalPages}
                      </p>
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative box-border flex shrink-0 cursor-pointer flex-row content-stretch items-center justify-center gap-2 overflow-visible rounded border px-3 py-1.5 transition-colors ${
                        currentPage === totalPages
                          ? "cursor-not-allowed border-[#c8d6df] bg-white text-[#c8d6df]"
                          : "border-[#0d9da4] bg-white text-[#0d9da4] hover:bg-[#f5fafd]"
                      }`}>
                      <div className="relative shrink-0 text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0]">
                        <p className="block whitespace-pre leading-[20px]">Next</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              <div
                data-reviews-container
                className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-3 p-0">
                {availableReviews.map((review) => (
                  <div
                    key={review.id}
                    className={`relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start overflow-hidden text-clip rounded-lg border p-4 transition-colors ${
                      (formData.settings.selectedReviewIds || []).includes(review.id)
                        ? "border-[#0d9da4] bg-[#f5fafd]"
                        : "border-[#c8d6df] hover:border-[#0d9da4]"
                    }`}>
                    {/* Checkbox at the top */}
                    <div className="relative mb-3 box-border flex w-full shrink-0 flex-row content-stretch items-center justify-between p-0">
                      <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[0] text-[#0b3954]">
                        <p className="block whitespace-pre leading-[20px]">Select this review</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={(formData.settings.selectedReviewIds || []).includes(review.id)}
                        onChange={() => handleReviewSelection(review.id)}
                        className="relative size-5 shrink-0 accent-[#0d9da4]"
                      />
                    </div>

                    {/* Review content */}
                    <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-start justify-start gap-4 p-0">
                      <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-2 p-0">
                        <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-2 p-0">
                          <div className="relative shrink-0 text-left font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-[#0b3954]">
                            <p className="block leading-[24px]">{getClientName(review)}</p>
                          </div>
                          {formData.settings.showRatings && (
                            <div className="relative box-border flex shrink-0 flex-row content-stretch items-center justify-start gap-0.5 p-0">
                              {renderStars(review.rating || review.score || review.stars || 0)}
                            </div>
                          )}
                        </div>
                        <div className="relative box-border flex shrink-0 flex-col content-stretch items-start justify-start gap-2 overflow-hidden p-0">
                          <div className="relative shrink-0 overflow-hidden text-left font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-[#0b3954]">
                            <p className="block break-words leading-[24px]">
                              &quot;{review.review || review.content || ""}&quot;
                            </p>
                          </div>
                          <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                            <p className="block leading-[19.5px]">
                              {review.createdAt
                                ? new Date(review.createdAt).toLocaleDateString()
                                : review.created_at
                                ? new Date(review.created_at).toLocaleDateString()
                                : ""}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-center justify-center gap-2 text-clip rounded-lg border-2 border-dashed border-[#c8d6df] bg-white py-8">
              <div className="relative shrink-0 text-center font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[0] text-[#55778a]">
                <p className="block leading-[24px]">No reviews found</p>
              </div>
              <div className="relative shrink-0 text-center font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                <p className="block leading-[19.5px]">Client reviews will appear here once you receive them</p>
              </div>
            </div>
          )}

          <div className="relative box-border flex w-full shrink-0 flex-col content-stretch items-start justify-start gap-1 p-0">
            <div className="relative box-border flex w-full shrink-0 flex-row content-stretch items-center justify-start gap-1 text-clip rounded-lg bg-[#f5fafd] p-3">
              <div className="relative shrink-0 text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-[#55778a]">
                <p className="block leading-[19.5px]">
                  <span className="font-['Manrope:Bold',_sans-serif] font-bold">Selected:</span>{" "}
                  {(formData.settings.selectedReviewIds || []).length} review
                  {(formData.settings.selectedReviewIds || []).length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Background Color Section */}
        <BackgroundColorToggle
          formData={formData}
          onSettingsChange={handleSettingsChange}
          sectionType="reviews"
          showHeaderContent={false}
        />
      </div>

      <EditorFooter
        onDelete={onDelete}
        onCancel={handleCancelClick}
        onSave={handleSubmit}
        saveLabel={isEditing ? "Update Reviews" : "Add Reviews"}
        showDelete={isEditing}
      />

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

export default ReviewsEditor
